import { useEffect, useRef, useState } from 'react';
import {
  DateValue,
  mergeProps,
  useDateField,
  useDatePicker,
  useFocusWithin,
  useLocale,
} from 'react-aria';
import { useDateFieldState, useDatePickerState } from 'react-stately';

import { CalendarDate, createCalendar } from '@internationalized/date';

import {
  getAutofixedCalendarDate,
  getErrorMessage,
  isValidDate,
  toCalendarDate,
  toISODateString,
} from '~/components/scales/DateInput/utils';
import { StrictUnion } from '~/utilities/types';

type BaseProps = {
  'aria-label'?: string;
  'data-cy'?: string;
  defaultValue?: string | null;
  isDateUnavailable?: (date: DateValue) => boolean;
  isDisabled?: boolean;
  label?: string;
  maxValue?: string | null;
  minValue?: string | null;
  unavailableMessage?: string;
  value?: string | null;
};

export type ClearableProps = {
  isClearable: true | boolean;
  onBlur?: (dueDate: string | null, dueDateOnly: string | null) => void;
  onChange?: (dueDate: string | null, dueDateOnly: string | null) => void;
};
export type NotClearableProps = {
  isClearable?: false;
  onBlur?: (dueDate: string, dueDateOnly: string) => void;
  onChange?: (dueDate: string, dueDateOnly: string) => void;
};

type Props = BaseProps & StrictUnion<ClearableProps | NotClearableProps>;

/**
 * Custom hook for managing a date input component that supports both segmented
 * date entry (via DateField) and calendar popup selection (via DatePicker)
 *
 * This hook coordinates two separate state managers:
 * - DatePicker: Handles calendar popup, date selection, and validation.
 * - DateField: Enables segmented date input for direct typing, which DatePicker
 *   alone does not support.
 *
 * It also includes custom autofix logic for incomplete or ambiguous dates
 */
export function useDateInput(props: Props) {
  const [value, setValue] = useState(toCalendarDate(props.defaultValue) ?? null);
  useEffect(() => {
    if (props.value === undefined) return;
    setValue((prevValue) => {
      const newValue = toCalendarDate(props.value) ?? null;
      if (prevValue === null && newValue === null) return prevValue;
      if (prevValue && newValue && prevValue.compare(newValue) === 0) return prevValue;
      return newValue;
    });
  }, [props.value]);

  const minValue = toCalendarDate(props.minValue) ?? undefined;
  const maxValue = toCalendarDate(props.maxValue) ?? undefined;
  const { isDateUnavailable } = props;
  const errorMessage = getErrorMessage(
    value,
    minValue,
    maxValue,
    isDateUnavailable,
    props.unavailableMessage
  );
  const [showError, setShowError] = useState(false); // only show error message after blur

  const onChange = (newValue: CalendarDate | null) => {
    if (newValue) {
      setValue(newValue);
      if (isValidDate(newValue, minValue, maxValue, isDateUnavailable))
        props.onChange?.(toISODateString(newValue), newValue.toString());
    } else if (props.isClearable) {
      setValue(null);
      props.onChange?.(null, null);
    }
  };
  const onBlur = (newValue: CalendarDate | null) => {
    if (newValue) {
      if (isValidDate(newValue, minValue, maxValue, isDateUnavailable))
        props.onBlur?.(toISODateString(newValue), newValue.toString());
    } else if (props.isClearable) {
      props.onBlur?.(null, null);
    }
  };

  // Date Picker: Manages calendar popup interactions, date selection,
  // validation, and accessibility. Coordinates with DateField for segmented
  // input, ensuring both direct typing and calendar selection work seamlessly.
  const pickerProps = {
    'aria-label': props['aria-label'],
    errorMessage,
    isDateUnavailable,
    isDisabled: props.isDisabled,
    isInvalid: Boolean(errorMessage),
    label: props.label,
    maxValue,
    minValue,
    onBlur: () => onBlur(value),
    onChange,
    value,
  };
  const pickerState = useDatePickerState(pickerProps);
  const pickerRef = useRef<HTMLDivElement>(null);
  const {
    buttonProps,
    calendarProps: pickerCalendarProps,
    errorMessageProps,
    fieldProps: pickerFieldProps,
    groupProps,
    isInvalid,
    labelProps,
  } = useDatePicker(pickerProps, pickerState, pickerRef);
  const calendarProps = mergeProps(pickerCalendarProps, {
    onChange: onBlur,
  });

  // Date Field: Manages segmented date input for direct typing (day/month/year fields),
  // using react-aria's useDateField and useDateFieldState. Coordinates with DatePicker
  // to ensure both calendar selection and manual entry work seamlessly.
  const fieldRef = useRef<HTMLDivElement>(null);
  const fieldState = useDateFieldState({
    ...pickerFieldProps,
    createCalendar,
    locale: useLocale().locale,
  });
  const { fieldProps } = useDateField(pickerFieldProps, fieldState, fieldRef);

  // Autofix logic: Automatically fills in missing date segments (e.g., year)
  // when the user focuses out of the input. This helps users complete partial
  // dates like "2/14" by assuming the current year or next year if the date has
  // already passed. This also prevents against users using a two-digit year.
  //
  // The way this works is by using useFocusWithin to detect when the user
  // focuses out of the date field segments, and injecting that behavior into
  // the groupProps provided by useDatePicker.
  const onAutoFix = () => {
    const [month, day, year] = [
      fieldState.segments.find((segment) => segment.type === 'month' && !segment.isPlaceholder)
        ?.value,
      fieldState.segments.find((segment) => segment.type === 'day' && !segment.isPlaceholder)
        ?.value,
      fieldState.segments.find((segment) => segment.type === 'year' && !segment.isPlaceholder)
        ?.value,
    ];
    const autofixedValue = getAutofixedCalendarDate(year, month, day);
    if (autofixedValue && isValidDate(autofixedValue, minValue, maxValue, isDateUnavailable)) {
      onChange?.(autofixedValue);
      onBlur?.(autofixedValue);
    }
  };
  const { focusWithinProps } = useFocusWithin({
    onFocusWithin: () => setShowError(false),
    onBlurWithin: () => {
      setShowError(true);
      onAutoFix();
    },
  });

  return {
    buttonProps,
    calendarProps,
    errorMessage: showError ? errorMessage : undefined,
    errorMessageProps,
    fieldProps,
    fieldRef,
    fieldState,
    groupProps: mergeProps(groupProps, focusWithinProps),
    groupRef: pickerRef,
    groupState: pickerState,
    isInvalid,
    labelProps,
    onClear: () => {
      onChange(null);
      onBlur(null);
    },
  };
}
